import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, FileText, Check, X, AlertTriangle, Shield } from 'lucide-react';

export default function ContentGuidelines() {
  const navigate = useNavigate();
  const lastUpdated = 'March 2026';

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-background-primary)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2 transition-colors"
            style={{ color: 'var(--fy-text-secondary)' }}
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden" style={{ backgroundColor: 'var(--fy-background-secondary)' }}>
              <img src="https://customer-assets.emergentagent.com/job_kindplatform/artifacts/my73uiau_file_00000000f09c720c9463ab506718d330.png" alt="LikeKind" className="w-8 h-8 object-cover" />
            </div>
            <span className="font-bold" style={{ color: 'var(--fy-text-primary)' }}>LikeKind</span>
          </div>
          <div className="w-20"></div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        {/* Title */}
        <div className="flex items-center gap-3 mb-2">
          <FileText className="w-8 h-8" style={{ color: 'var(--lk-sage)' }} />
          <h1 
            className="text-3xl sm:text-4xl font-bold" 
            style={{ color: 'var(--fy-text-primary)', fontFamily: 'Outfit' }}
          >
            Content Guidelines
          </h1>
        </div>
        <p className="mb-10" style={{ color: 'var(--fy-text-disabled)' }}>
          Last updated: {lastUpdated}
        </p>

        {/* Content */}
        <div className="space-y-10">
          
          {/* Intro */}
          <section>
            <p className="text-lg leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              LikeKind is built for coordination, community participation, and real-world connection.
            </p>
            <p className="mt-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              These guidelines explain what belongs on LikeKind and what does not.
            </p>
          </section>

          {/* What Belongs */}
          <section>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--fy-text-primary)' }}>
              <Check className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
              What Belongs on LikeKind
            </h2>
            <p className="mb-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              Content should support:
            </p>
            <ul className="space-y-2" style={{ color: 'var(--fy-text-secondary)' }}>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--lk-sage)' }} />
                Local activities
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--lk-sage)' }} />
                Group coordination
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--lk-sage)' }} />
                Community events
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--lk-sage)' }} />
                Helpful information
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--lk-sage)' }} />
                Real-world participation
              </li>
            </ul>
            <p className="mt-4 font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              LikeKind is not designed for performance or attention-seeking content.
            </p>
          </section>

          {/* What Does Not Belong */}
          <section>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--fy-text-primary)' }}>
              <X className="w-5 h-5" style={{ color: '#DC2626' }} />
              What Does Not Belong
            </h2>
            <p className="mb-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              Content may be removed if it:
            </p>
            <ul className="space-y-2" style={{ color: 'var(--fy-text-secondary)' }}>
              <li className="flex items-start gap-2">
                <X className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#DC2626' }} />
                Harasses or intimidates others
              </li>
              <li className="flex items-start gap-2">
                <X className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#DC2626' }} />
                Spreads harmful misinformation
              </li>
              <li className="flex items-start gap-2">
                <X className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#DC2626' }} />
                Attempts to exploit or manipulate community trust
              </li>
              <li className="flex items-start gap-2">
                <X className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#DC2626' }} />
                Promotes illegal activity
              </li>
              <li className="flex items-start gap-2">
                <X className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#DC2626' }} />
                Attempts to target or exploit minors
              </li>
            </ul>
          </section>

          {/* Child Safety */}
          <section className="p-5 rounded-xl" style={{ backgroundColor: '#FEF3C7', border: '1px solid #F59E0B' }}>
            <h2 className="text-xl font-bold mb-3 flex items-center gap-2" style={{ color: '#92400E' }}>
              <AlertTriangle className="w-5 h-5" />
              Child Safety
            </h2>
            <p className="leading-relaxed mb-3" style={{ color: '#92400E' }}>
              Content involving the exploitation, targeting, or endangerment of minors is strictly prohibited.
            </p>
            <p className="leading-relaxed font-bold" style={{ color: '#92400E' }}>
              Violations involving child safety may result in immediate permanent removal from the platform.
            </p>
          </section>

          {/* Respectful Participation */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              Respectful Participation
            </h2>
            <p className="mb-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              LikeKind is designed for calm communication.
            </p>
            <p className="mb-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              Content intended to provoke conflict, hostility, or disruption may be removed.
            </p>
            <p className="leading-relaxed font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              Disagreement is allowed. Harassment is not.
            </p>
          </section>

          {/* Misrepresentation */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              Misrepresentation
            </h2>
            <p className="mb-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              Users may not:
            </p>
            <ul className="space-y-2 mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
              <li className="flex items-start gap-2">
                <X className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--fy-text-disabled)' }} />
                Impersonate others
              </li>
              <li className="flex items-start gap-2">
                <X className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--fy-text-disabled)' }} />
                Misrepresent organizations
              </li>
              <li className="flex items-start gap-2">
                <X className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--fy-text-disabled)' }} />
                Attempt to manipulate group coordination through deception
              </li>
            </ul>
            <p className="leading-relaxed font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              Trust within communities is essential to the platform.
            </p>
          </section>

          {/* Spam and Manipulation */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              Spam and Manipulation
            </h2>
            <p className="mb-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              LikeKind is not designed for promotional flooding or engagement manipulation.
            </p>
            <p className="leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              Content that attempts to artificially influence participation or attention may be restricted.
            </p>
          </section>

          {/* Enforcement */}
          <section>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--fy-text-primary)' }}>
              <Shield className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
              Enforcement
            </h2>
            <p className="mb-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              Enforcement may include:
            </p>
            <ul className="space-y-2 mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
              <li>• Content removal</li>
              <li>• Account restrictions</li>
              <li>• Account suspension</li>
              <li>• Permanent removal in serious cases</li>
            </ul>
            <p className="leading-relaxed font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              Child safety violations may result in immediate permanent removal.
            </p>
          </section>

          {/* Our Philosophy */}
          <section className="p-6 rounded-xl" style={{ backgroundColor: 'var(--fy-background-secondary)', border: '1px solid var(--fy-border-subtle)' }}>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              Our Philosophy
            </h2>
            <p className="mb-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              LikeKind does not attempt to eliminate all harmful behavior.
            </p>
            <p className="mb-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              Instead, it reduces risk through structure, visibility, and community design.
            </p>
            <p className="leading-relaxed font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              Trust is built through constraints.
            </p>
          </section>

          {/* Related Links */}
          <section className="pt-6" style={{ borderTop: '1px solid var(--fy-border-subtle)' }}>
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              Related
            </h3>
            <div className="flex flex-wrap gap-3">
              <Link 
                to="/trust" 
                className="px-4 py-2 rounded-full text-sm transition-colors"
                style={{ backgroundColor: 'var(--fy-background-secondary)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}
              >
                Trust & Safety
              </Link>
              <Link 
                to="/privacy" 
                className="px-4 py-2 rounded-full text-sm transition-colors"
                style={{ backgroundColor: 'var(--fy-background-secondary)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}
              >
                Privacy Policy
              </Link>
              <Link 
                to="/terms" 
                className="px-4 py-2 rounded-full text-sm transition-colors"
                style={{ backgroundColor: 'var(--fy-background-secondary)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}
              >
                Terms of Service
              </Link>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="mt-16 py-8" style={{ borderTop: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p style={{ color: 'var(--fy-text-disabled)' }}>
            © {new Date().getFullYear()} LikeKind Inc. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
